import React, { type ErrorInfo, useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { useAnalytics } from '../../../analytics/use-analytics';
import { useEditionAwareness } from '../../../services/edition-awareness-context';
import { getHamsKey } from '../../../services/product-to-hams-key';
import { ModalEntryPoint } from '../shared/modal-entry-point';

import { DIALOG_CONFIG } from './dialog-config';
import { entryPoint, type EntryPointParams } from './entrypoint';

export function FreeChooseYourEditionModal() {
	const { product, cloudId, showFlag } = useEditionAwareness();
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const entryPointParams: EntryPointParams = useMemo(
		() => ({
			cloudId,
			hamsProductKey: getHamsKey(product),
		}),
		[cloudId, product],
	);

	const modalProps = useMemo(
		() => ({
			autoFocus: false,
			shouldScrollInViewport: true,
			width: 'x-large',
		}),
		[],
	);

	const handleOnError = useCallback(
		({ error, errorInfo }: { error: Error; errorInfo: ErrorInfo }) => {
			showFlag?.({
				id: '@atlassian/edition-awareness.dialog.free-choose-your-edition.entrypoint.flag',
				title: formatMessage({
					id: '@atlassian/edition-awareness.dialog.free-choose-your-edition.entrypoint.flag.title',
					defaultMessage: "We weren't able to get your plan details.",
					description:
						'Title for the error flag shown when Edition Awareness entrypoint cannot be loaded.',
				}),
				description: '',
				appearance: 'error',
				actions: [
					{
						content: formatMessage({
							id: '@atlassian/edition-awareness.dialog.free-choose-your-edition.entrypoint.flag.action',
							defaultMessage: 'Refresh',
							description:
								'Action for the error flag shown when Edition Awareness entrypoint cannot be loaded.',
						}),
						onClick: () => {
							window.location.reload();
						},
					},
				],
			});

			sendAnalyticsEvent({
				eventType: 'operational',
				action: 'failed',
				actionSubject: 'entrypoint',
				actionSubjectId: '@atlassian/edition-awareness.dialog.free-choose-your-edition.entrypoint',
				attributes: {
					error,
					errorInfo,
				},
			});
		},
		[showFlag, sendAnalyticsEvent, formatMessage],
	);

	return (
		<ModalEntryPoint
			entryPoint={entryPoint}
			entryPointParams={entryPointParams}
			modalProps={modalProps}
			dialogConfig={DIALOG_CONFIG}
			onError={handleOnError}
		/>
	);
}
